/**
 * ActivityHeatmap — 14-day × 24-hour heatmap of scraper activity.
 *
 * Reads scraper_activity collection (doc IDs: YYYY-MM-DD-HH, Berlin time),
 * renders a grid with the total action count per hour + red border on hours
 * where a CAPTCHA was detected. Hover shows breakdown of stages/notes/messages.
 *
 * Written into by scraper's bump_activity() helper — see scraper/orders_scraper.py.
 * Data starts flowing after the first sync after deploy; historical hours before
 * that appear empty (which is fine — collection is forward-looking).
 */
import React, { useEffect, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";

const DAYS = 14;

// Color thresholds. Anpassbar wenn Aktivitätsniveau sich ändert.
function bgColor(n) {
  if (n === 0)  return "transparent";
  if (n < 5)   return "#edf2f7";
  if (n < 15)  return "#c3dafe";
  if (n < 30)  return "#7f9cf5";
  return "#553c9a";
}
function fgColor(n) {
  return n >= 30 ? "#ffffff" : "#1a202c";
}

// Berlin-Zeit-Bucket IDs für die letzten DAYS Tage
function berlinTodayBucket() {
  // Format YYYY-MM-DD in Berlin time
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Berlin", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date());
  const y = parts.find(p => p.type === "year").value;
  const m = parts.find(p => p.type === "month").value;
  const d = parts.find(p => p.type === "day").value;
  return `${y}-${m}-${d}`;
}

// Baut Datumsliste [today-13, today-12, ..., today]
function buildDateList() {
  const today = berlinTodayBucket();
  const [y, m, d] = today.split("-").map(Number);
  const base = new Date(Date.UTC(y, m - 1, d));
  const list = [];
  for (let i = DAYS - 1; i >= 0; i--) {
    const dt = new Date(base.getTime() - i * 86400000);
    const yy = dt.getUTCFullYear();
    const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(dt.getUTCDate()).padStart(2, "0");
    list.push(`${yy}-${mm}-${dd}`);
  }
  return list;
}

function formatDay(dayStr) {
  const [, m, d] = dayStr.split("-");
  return `${d}.${m}.`;
}

function isTodayBerlin(dayStr) {
  return dayStr === berlinTodayBucket();
}

function berlinDayLabel(dayStr) {
  const [y, m, d] = dayStr.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const w = dt.toLocaleDateString("de-DE", { weekday: "short", timeZone: "UTC" });
  return `${w} ${formatDay(dayStr)}`;
}

export default function ActivityHeatmap() {
  const [buckets, setBuckets] = useState({});   // { "2026-07-24-13": { stageChanges, notes, messages, captchas } }
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Subscribe to whole collection — small (< 500 docs), simple query,
    // real-time updates so the admin sees new syncs land as they finish.
    const unsub = onSnapshot(
      collection(db, "scraper_activity"),
      (snap) => {
        const map = {};
        snap.forEach(d => { map[d.id] = d.data(); });
        setBuckets(map);
        setLoading(false);
      },
      (err) => {
        console.error("ActivityHeatmap subscribe failed:", err);
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  const days = buildDateList();
  const hours = Array.from({ length: 24 }, (_, i) => i);

  return (
    <div style={{
      background: "#fff",
      borderRadius: "12px",
      boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
      padding: "1.5em",
      marginBottom: "1.5em",
    }}>
      <div style={{
        fontSize: "0.75rem",
        color: "#4a5568",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        marginBottom: "0.6em",
      }}>
        Scraper-Aktivität ({DAYS} Tage)
      </div>

      {loading ? (
        <p style={{ fontSize: "0.85rem", color: "#718096" }}>Lade Daten…</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{
            borderCollapse: "separate",
            borderSpacing: "2px",
            fontSize: "0.72rem",
            fontFamily: "'SFMono-Regular', 'Menlo', monospace",
          }}>
            <thead>
              <tr>
                <th style={{ width: 66, textAlign: "left", padding: "2px 6px 2px 0", fontWeight: 500, color: "#718096" }}></th>
                {hours.map(h => (
                  <th key={h} style={{
                    width: 26, textAlign: "center", fontWeight: 500,
                    color: "#a0aec0", padding: "1px 0",
                  }}>
                    {String(h).padStart(2, "0")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {days.map(day => (
                <tr key={day}>
                  <td style={{
                    padding: "2px 8px 2px 0",
                    color: isTodayBerlin(day) ? "#553c9a" : "#4a5568",
                    fontWeight: isTodayBerlin(day) ? 700 : 500,
                    whiteSpace: "nowrap",
                    fontFamily: "system-ui, -apple-system, sans-serif",
                    fontSize: "0.78rem",
                    textAlign: "right",
                  }}>
                    {berlinDayLabel(day)}
                  </td>
                  {hours.map(h => {
                    const key = `${day}-${String(h).padStart(2, "0")}`;
                    const b = buckets[key] || {};
                    const s = b.stageChanges || 0;
                    const n = b.notes || 0;
                    const m = b.messages || 0;
                    const c = b.captchas || 0;
                    const total = s + n + m;
                    const hasCaptcha = c > 0;
                    const label = total === 0 ? "·" : `${total}`;
                    return (
                      <td key={key} style={{
                        width: 26, height: 24,
                        background: bgColor(total),
                        color: total === 0 ? "#cbd5e0" : fgColor(total),
                        border: hasCaptcha
                          ? "2px solid #e53e3e"
                          : total === 0
                            ? "1px solid transparent"
                            : "1px solid #e2e8f0",
                        borderRadius: "3px",
                        textAlign: "center",
                        cursor: total > 0 ? "help" : "default",
                        fontWeight: hasCaptcha ? 700 : (total >= 15 ? 600 : 400),
                      }} title={
                        total === 0 && !hasCaptcha
                          ? `${berlinDayLabel(day)} ${String(h).padStart(2, "0")}:00 — keine Aktivität`
                          : `${berlinDayLabel(day)} ${String(h).padStart(2, "0")}:00\n`
                            + `Stages: ${s}\nNotizen: ${n}\nNachrichten: ${m}`
                            + (hasCaptcha ? `\n⚠ CAPTCHA` : "")
                      }>
                        {hasCaptcha && total === 0 ? "⚠" : label}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Daily-total line chart (same 14-day window) ── */}
      {!loading && (() => {
        // Aggregate per-day totals once
        const dayStats = days.map(day => {
          let stages = 0, notes = 0, messages = 0, captchas = 0;
          for (const h of hours) {
            const b = buckets[`${day}-${String(h).padStart(2, "0")}`] || {};
            stages   += b.stageChanges || 0;
            notes    += b.notes        || 0;
            messages += b.messages     || 0;
            captchas += b.captchas     || 0;
          }
          return { day, stages, notes, messages, captchas,
                   total: stages + notes + messages };
        });
        const maxTotal = Math.max(50, ...dayStats.map(d => d.total));

        // SVG geometry — responsive via viewBox
        const W = 720, H = 170;
        const padL = 34, padR = 12, padT = 12, padB = 30;
        const chartW = W - padL - padR;
        const chartH = H - padT - padB;
        const xStep = chartW / (DAYS - 1);
        const x = i => padL + i * xStep;
        const y = v => padT + chartH - (v / maxTotal) * chartH;

        // Line path
        const points = dayStats.map((d, i) => `${x(i)},${y(d.total)}`);
        const linePath = "M " + points.join(" L ");

        // Filled area under the line (subtle purple fill)
        const areaPath = linePath
          + ` L ${x(DAYS - 1)},${padT + chartH}`
          + ` L ${x(0)},${padT + chartH} Z`;

        // Y-axis reference values
        const yTicks = [0, Math.round(maxTotal / 2), maxTotal];

        return (
          <div style={{ marginTop: "1.5em" }}>
            <div style={{
              fontSize: "0.72rem",
              color: "#4a5568",
              fontWeight: 600,
              marginBottom: "0.4em",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}>
              Tagessumme (Aktionen/Tag)
            </div>
            <div style={{ overflowX: "auto" }}>
              <svg viewBox={`0 0 ${W} ${H}`}
                   preserveAspectRatio="xMidYMid meet"
                   style={{ width: "100%", minWidth: 620, height: "auto",
                            display: "block" }}>
                {/* Grid lines */}
                {yTicks.map(v => (
                  <line key={`grid-${v}`}
                        x1={padL} x2={W - padR}
                        y1={y(v)} y2={y(v)}
                        stroke="#e2e8f0"
                        strokeDasharray={v === 0 ? "" : "2,3"}
                        strokeWidth="1" />
                ))}
                {/* Y-axis labels */}
                {yTicks.map(v => (
                  <text key={`ylab-${v}`}
                        x={padL - 6} y={y(v) + 4}
                        textAnchor="end"
                        fontSize="10"
                        fill="#a0aec0"
                        fontFamily="system-ui, sans-serif">
                    {v}
                  </text>
                ))}
                {/* Area fill */}
                <path d={areaPath}
                      fill="#553c9a"
                      fillOpacity="0.08"
                      stroke="none" />
                {/* Line */}
                <path d={linePath}
                      fill="none"
                      stroke="#553c9a"
                      strokeWidth="2"
                      strokeLinejoin="round"
                      strokeLinecap="round" />
                {/* Data points */}
                {dayStats.map((d, i) => {
                  const isToday = isTodayBerlin(d.day);
                  const hasCaptcha = d.captchas > 0;
                  return (
                    <g key={d.day}>
                      <circle cx={x(i)} cy={y(d.total)}
                              r={hasCaptcha ? 5 : (isToday ? 4.5 : 3.5)}
                              fill={hasCaptcha ? "#e53e3e" : "#553c9a"}
                              stroke="#fff"
                              strokeWidth="1.5">
                        <title>
                          {`${berlinDayLabel(d.day)} — Gesamt: ${d.total}`
                            + `\nStages: ${d.stages}\nNotizen: ${d.notes}\nNachrichten: ${d.messages}`
                            + (hasCaptcha ? `\n⚠ CAPTCHAs: ${d.captchas}` : "")}
                        </title>
                      </circle>
                      {/* Value label above dot for CAPTCHA-days or peaks */}
                      {(hasCaptcha || d.total === maxTotal) && d.total > 0 && (
                        <text x={x(i)} y={y(d.total) - 9}
                              textAnchor="middle"
                              fontSize="10"
                              fontWeight="700"
                              fill={hasCaptcha ? "#e53e3e" : "#553c9a"}
                              fontFamily="system-ui, sans-serif">
                          {d.total}
                        </text>
                      )}
                    </g>
                  );
                })}
                {/* X-axis date labels — every 2nd to avoid crowding */}
                {dayStats.map((d, i) => {
                  if (DAYS > 10 && i % 2 !== 0 && i !== DAYS - 1) return null;
                  const [, mm, dd] = d.day.split("-");
                  const isToday = isTodayBerlin(d.day);
                  return (
                    <text key={`xlab-${d.day}`}
                          x={x(i)} y={H - 12}
                          textAnchor="middle"
                          fontSize="10"
                          fill={isToday ? "#553c9a" : "#a0aec0"}
                          fontWeight={isToday ? 700 : 400}
                          fontFamily="system-ui, sans-serif">
                      {dd}.{mm}.
                    </text>
                  );
                })}
              </svg>
            </div>
          </div>
        );
      })()}

      {/* Legend */}
      <div style={{
        marginTop: "1em",
        fontSize: "0.72rem",
        color: "#718096",
        display: "flex",
        alignItems: "center",
        gap: "1em",
        flexWrap: "wrap",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.4em" }}>
          <span>Aktivität:</span>
          {[
            { l: "0",     bg: "transparent",  c: "#cbd5e0", border: "1px solid #e2e8f0" },
            { l: "1-4",   bg: "#edf2f7",      c: "#1a202c" },
            { l: "5-14",  bg: "#c3dafe",      c: "#1a202c" },
            { l: "15-29", bg: "#7f9cf5",      c: "#1a202c" },
            { l: "30+",   bg: "#553c9a",      c: "#ffffff" },
          ].map(item => (
            <span key={item.l} style={{
              background: item.bg,
              color: item.c,
              border: item.border || "1px solid #e2e8f0",
              borderRadius: "3px",
              padding: "1px 6px",
              fontFamily: "'SFMono-Regular', monospace",
            }}>
              {item.l}
            </span>
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.4em" }}>
          <span style={{
            border: "2px solid #e53e3e",
            borderRadius: "3px",
            padding: "1px 6px",
            fontWeight: 700,
            color: "#e53e3e",
            fontFamily: "'SFMono-Regular', monospace",
          }}>
            ⚠
          </span>
          <span>CAPTCHA</span>
        </div>
        <span style={{ marginLeft: "auto", color: "#a0aec0" }}>
          Berlin-Zeit · Hover für Details
        </span>
      </div>
    </div>
  );
}
